/**
 * Is Dr. Clinix (DrWeb) connected for the current account? Feeds the `drweb` branch of the
 * conversation sidebar (plan D4/D5: fail closed — no live connection, no accordion).
 *
 * One `GET smarticks/drweb/status` per account, cached at module scope for STATUS_TTL_MS, so the
 * sidebar never fires a round-trip per conversation. Kept as a composable rather than a Vuex
 * module on purpose: registering a module means a third in-place edit of an upstream file
 * (`store/index.js`), and the panel only needs one boolean.
 *
 * The 5-minute TTL (QA R-4) is what makes a connection enabled in super_admin show up in an
 * already-open tab without a reload: the next conversation opened after the TTL re-asks. The
 * last-known answer stays visible while the refresh is in flight.
 *
 * A failed FIRST call answers `false` (fail closed: no live connection, no accordion) and is
 * retried on the next mount — a 401 from an expired session must not pin "disabled" for the rest
 * of the day. A failed REFRESH keeps the last-known answer and also retries on the next mount.
 */
import { computed, reactive } from 'vue';
import { useStore } from 'dashboard/composables/store';
import SmarticksAPI from 'dashboard/api/smarticks';

export const STATUS_TTL_MS = 5 * 60 * 1000;

// accountId -> { enabled: boolean, tenantUrl: string|null, loaded: boolean, loadedAt: number }
const cache = reactive({});
const inflight = {};

const isFresh = entry =>
  entry?.loaded === true && Date.now() - (entry.loadedAt || 0) < STATUS_TTL_MS;

const fetchStatus = async accountId => {
  if (!accountId || inflight[accountId]) return;
  inflight[accountId] = SmarticksAPI.drwebStatus()
    .then(({ data }) => {
      cache[accountId] = {
        enabled: data?.ok === true && data?.enabled === true,
        tenantUrl: data?.tenant_url || null,
        loaded: true,
        loadedAt: Date.now(),
      };
    })
    .catch(() => {
      // loaded:false → the next mount retries; the last-known answer (if any) stays.
      cache[accountId] = {
        enabled: cache[accountId]?.enabled === true,
        tenantUrl: cache[accountId]?.tenantUrl || null,
        loaded: false,
        loadedAt: 0,
      };
    })
    .finally(() => {
      delete inflight[accountId];
    });
};

export function useDrwebStatus() {
  const store = useStore();
  const accountId = computed(() => store.getters.getCurrentAccountId);

  const entry = computed(() => cache[accountId.value]);
  if (!isFresh(entry.value)) fetchStatus(accountId.value);

  return {
    isDrwebEnabled: computed(() => entry.value?.enabled === true),
    drwebTenantUrl: computed(() => entry.value?.tenantUrl || null),
    refreshDrwebStatus: () => fetchStatus(accountId.value),
  };
}
